import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "design-system/atoms/Button"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetTitle,
  SheetTrigger,
} from "design-system/atoms/Sheet"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { Form, FormControl, FormField, FormItem, FormMessage } from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { ArrowRight } from "lucide-react"
import { useSalesForceModelMapQuery } from "modules/settings/hooks/salesForce/useSalesForceModelMapQuery"
import { useUpdateSalesForceKeyMapMutation } from "modules/settings/hooks/salesForce/useUpdateSalesForceKeyMapMutation"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { SalesForceIntegrationModels } from "schema"
import { z } from "zod"

type ConfigureModelProps = {
  model: z.infer<typeof SalesForceIntegrationModels>
}

const FormSchema = z.record(z.string(), z.string().optional())

export const ConfigureModel = ({ model }: ConfigureModelProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation()
  const { data, isPending } = useSalesForceModelMapQuery({ model })
  const { mutateAsync } = useUpdateSalesForceKeyMapMutation()
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {},
  })

  const onSubmit = async (data: z.infer<typeof FormSchema>) => {
    const keyMap = Object.entries(data).filter((e) => e[1]) as [string, string][]

    await mutateAsync({
      model,
      body: {
        keyMap: keyMap.map((e) => ({ key: e[0], source: e[1] })),
      },
    })
    setIsOpen(false)
  }

  const onOpenChange = (isOpen: boolean) => {
    setIsOpen(isOpen)

    if (!isOpen) {
      form.reset()
    }
  }

  useEffect(() => {
    if (data && data.model) {
      form.reset(data.model.values)
    }
  }, [data, form])

  if (isPending) {
    return (
      <Button className="w-full" variant={"secondary"} onClick={() => setIsOpen(true)} disabled>
        {t("settings.integrations.salesForce.models.configureMapping")}
      </Button>
    )
  }

  return (
    <Sheet onOpenChange={onOpenChange} open={isOpen}>
      <SheetTrigger asChild>
        <Button className="w-full" variant={"secondary"} onClick={() => setIsOpen(true)}>
          {t("settings.integrations.salesForce.models.configureMapping")}
        </Button>
      </SheetTrigger>

      <SheetContent className="min-w-full! md:min-w-[600px]!">
        <div className="flex flex-col gap-6">
          <div>
            <SheetTitle>{t("settings.integrations.salesForce.models.title")}</SheetTitle>
            <SheetDescription>
              {t("settings.integrations.salesForce.models.description")}
            </SheetDescription>
          </div>
          <Form
            {...form}
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
            errorTranslations={getErrorTranslations()}
          >
            <div className="flex flex-col gap-2">
              {data?.model?.fields.map((fieldName) => (
                <div
                  key={fieldName}
                  className="grid grid-cols-7 gap-2 hover:bg-accent p-2 rounded-md text-sm -mx-2"
                >
                  <div className="col-span-2 items-center flex">
                    <span>{fieldName}</span>
                  </div>
                  <div className="col-span-1 items-center flex">
                    <ArrowRight size={16} />
                  </div>
                  <div className="col-span-4">
                    <FormField
                      control={form.control}
                      name={fieldName}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Select
                              value={field.value}
                              onValueChange={(value) => {
                                field.onChange(value)
                                form.setValue(fieldName, value)
                                form.trigger()
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue
                                  placeholder={t(
                                    "settings.integrations.salesForce.models.sourcePlaceholder",
                                  )}
                                />
                              </SelectTrigger>
                              <SelectContent className="max-h-72">
                                {data.object.fields
                                  .sort((a, b) => (a.name < b.name ? -1 : 1))
                                  .map((field) => (
                                    <SelectItem key={field.name} value={field.name}>
                                      {field.name}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              ))}
            </div>
            <SheetFooter>
              <Button disabled={form.formState.isLoading} type="submit">
                {t("settings.integrations.salesForce.models.save")}
              </Button>
            </SheetFooter>
          </Form>
        </div>
      </SheetContent>
    </Sheet>
  )
}
